import { GRID_COLS, GRID_ROWS } from '../game/Playfield';

export const NUM_LEVELS = 500;
export const SPAWN_ZONE_ROWS = 4;
export const TOP_BUFFER_ROWS = 8;
export const ROWS_PER_LEVEL = 5;
export const MIN_LATERAL_SHIFT = 4;

const SPAWN_TOP_ROW = GRID_ROWS - SPAWN_ZONE_ROWS;
const BASE_SEED = 1;

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function cellKey(col: number, row: number): string {
  return `${col},${row}`;
}

export interface LevelLayout {
  target: { col: number; row: number };
  clearCells: Set<string>;
}

export function pathWidthForLevel(level: number): number {
  return level === 1 ? 3 : 1;
}

export function waypointRow(level: number): number {
  const safe = clamp(Math.floor(level), 1, NUM_LEVELS);
  return SPAWN_TOP_ROW - 1 - safe * ROWS_PER_LEVEL;
}

export function carvePathRow(
  clear: Set<string>,
  col: number,
  row: number,
  width: number,
): void {
  const half = Math.floor((width - 1) / 2);
  const lo = col - half;
  const hi = lo + width - 1;
  for (let c = lo; c <= hi; c++) {
    if (c >= 0 && c < GRID_COLS) clear.add(cellKey(c, row));
  }
}

export function fillBetween(
  clear: Set<string>,
  c1: number,
  c2: number,
  row: number,
  width: number,
): void {
  const from = Math.min(c1, c2);
  const to = Math.max(c1, c2);
  for (let c = from; c <= to; c++) {
    carvePathRow(clear, c, row, width);
  }
}

// Branches stretch wide at low levels and collapse to a single trunk at the
// final level. Level 1 is special—flat arc (spread=1) for the on-ramp. Then
// linear ramp from spread 3 (L=2) to spread 1 (L=NUM_LEVELS-1), and 0 at
// L=NUM_LEVELS so the climax level forces one path only.
export function branchSpread(level: number): number {
  if (level === 1) return 1;
  if (level >= NUM_LEVELS) return 0;
  const t = (level - 1) / Math.max(1, NUM_LEVELS - 2);
  return clamp(Math.round(lerp(3, 1, t)), 1, 3);
}

interface FullClimb {
  cellsByLevel: Set<string>[];
  colByLevel: number[];
}

let climbCache: FullClimb | undefined;

function computeFullClimb(): FullClimb {
  if (climbCache) return climbCache;

  const rng = mulberry32(BASE_SEED);
  const colByLevel: number[] = [];
  colByLevel[0] = Math.floor(rng() * GRID_COLS);

  for (let L = 1; L <= NUM_LEVELS; L++) {
    const prev = colByLevel[L - 1];
    // Odd-row holes are staggered +CELL_W/2, putting col 9 against the right wall
    // at x=LOGICAL_WIDTH-PLAY_MARGIN_X — physically unreachable by the ball's
    // clamped center (gap exceeds FALL_IN_DIST). Even rows have the symmetric
    // problem at col 0. Exclude the unreachable column for the target's row parity.
    const rowEven = waypointRow(L) % 2 === 0;
    const forbiddenCol = rowEven ? 0 : GRID_COLS - 1;
    const candidates: number[] = [];
    for (let c = 0; c < GRID_COLS; c++) {
      if (c === forbiddenCol) continue;
      if (Math.abs(c - prev) >= MIN_LATERAL_SHIFT) candidates.push(c);
    }
    const idx = Math.floor(rng() * candidates.length);
    colByLevel[L] = candidates[idx];
  }

  const cellsByLevel: Set<string>[] = [new Set<string>()];
  for (let L = 1; L <= NUM_LEVELS; L++) {
    const segBottom = L === 1 ? SPAWN_TOP_ROW - 1 : waypointRow(L - 1);
    const segTop = waypointRow(L);
    const colBottom = colByLevel[L - 1];
    const colTop = colByLevel[L];
    const cells = new Set<string>();
    carveSegment(cells, segBottom, segTop, colBottom, colTop, branchSpread(L), pathWidthForLevel(L));
    cellsByLevel.push(cells);
  }

  climbCache = { cellsByLevel, colByLevel };
  return climbCache;
}

function carveSegment(
  cells: Set<string>,
  segBottom: number,
  segTop: number,
  colBottom: number,
  colTop: number,
  spread: number,
  width: number,
): void {
  const span = segBottom - segTop;

  // Endpoints lock to the joining columns: segBottom inherits the previous level's
  // waypoint column; segTop is this level's waypoint column.
  carvePathRow(cells, colBottom, segBottom, width);
  carvePathRow(cells, colTop, segTop, width);

  for (const sign of [-1, +1] as const) {
    let prevCol = colBottom;
    // midContinuous interpolates linearly along the segment from colBottom (k=0)
    // to colTop (k=span). peak is a sin half-cycle that's 0 at both endpoints and
    // 1 at the middle, so deviation maxes out at the middle row.
    for (let k = 1; k < span; k++) {
      const row = segBottom - k;
      const midContinuous = colBottom + ((colTop - colBottom) * k) / span;
      const peak = Math.sin((Math.PI * k) / span);
      const col = clamp(
        Math.round(midContinuous + sign * peak * spread),
        0,
        GRID_COLS - 1,
      );
      fillBetween(cells, prevCol, col, row, width);
      prevCol = col;
    }
    fillBetween(cells, prevCol, colTop, segTop, width);
  }
}

export function getLevelLayout(level: number): LevelLayout {
  const safe = clamp(Math.floor(level), 1, NUM_LEVELS);
  const climb = computeFullClimb();
  const clear = new Set<string>();

  for (let r = SPAWN_TOP_ROW; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) clear.add(cellKey(c, r));
  }
  for (let lvl = 1; lvl <= safe; lvl++) {
    for (const cell of climb.cellsByLevel[lvl]) clear.add(cell);
  }

  const targetCol = climb.colByLevel[safe];
  const targetRow = waypointRow(safe);
  clear.add(cellKey(targetCol, targetRow));

  return { target: { col: targetCol, row: targetRow }, clearCells: clear };
}

export function waypointCol(level: number): number {
  const safe = clamp(Math.floor(level), 0, NUM_LEVELS);
  return computeFullClimb().colByLevel[safe];
}
